// Services
import breedingManagementService from "@/app/services/main/breeding-management/breeding-management-service.js";

// Utils
import { vuelidateUtil } from "@/utils/vuelidate-util.js";

// Mixins
import { dateMixins } from "@/app/mixins/utils/date-mixins";
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { inputTextMixins } from "@/app/mixins/utils/input-text-mixins";
import { numberFormatMixins } from "@/app/mixins/utils/number-format-mixins";
import { unsavedChangesModalMixins } from "@/app/mixins/utils/unsaved-changes-mixins.js";

// Constants
import { STATUS_CODES, SUCCESS_MSG } from "@/app/constants/status-constants";
import { BREEDER_ROUTES } from "@/app/constants/breeder-routes-constants";
import { DAM_SIRE_DOCUMENT } from "@/app/constants/dam-sire-documents-constants";

const PUPPY_GENDER = {
    MALE: "Male",
    FEMALE: "Female",
};

export default {
    name: "AllocatePuppyContainer",
    components: {},
    directive: {},
    filters: {},
    mixins: [inputTextMixins, toastMessageMixins, dateMixins, numberFormatMixins, unsavedChangesModalMixins],
    props: {},
    data() {
        return {
            PUPPY_GENDER,
            isAPILoading: false,
            isAPISubmitLoading: false,
            isAllocatingSuccess: false,

            puppyList: [],
            selectedPuppy: null,
            isPuppyValid: true,
            phoneNumberIsValid: true,

            allocationDetails: {
                firstName: "",
                lastName: "",
                emailAddress: "",
                phoneNumber: "",
            },

            // needs to be separated from the allocationDetails for formatting purposes
            price: null,
            deposit: null,
            isDepositInvalid: false,

            contractDocument: null,
            ALLOWED_TYPES: DAM_SIRE_DOCUMENT.ALLOWED_TYPES,
        };
    },
    computed: {
        validationRules() {
            let fields = {
                allocationDetails: {
                    emailAddress: {},
                    phoneNumber: {},
                },
            };

            fields.allocationDetails = vuelidateUtil.setEmailValidation(fields.allocationDetails, "emailAddress");

            return fields;
        },
        computedBalance() {
            const isPriceInvalid = this.price === null || this.price === "";
            const isDepositInvalid = this.deposit === null || this.deposit === "";

            if (isPriceInvalid && isDepositInvalid) {
                return "";
            }

            const puppyPrice = isPriceInvalid ? 0 : parseFloat(this.price);
            const puppyDeposit = isDepositInvalid ? 0 : parseFloat(this.deposit);

            if (puppyPrice > puppyDeposit) {
                this.isDepositInvalid = false;
            }

            return puppyPrice - puppyDeposit;
        },
    },
    watch: {},
    async created() {
        if (this.$route.query) {
            this.matingId = this.$route.query.matingId;

            await this.getPuppyLitterList({limit: 15});
        }
    },
    methods: {
        async getPuppyLitterList() {
            this.isAPILoading = true;

            try {
                const response = await breedingManagementService.getPuppiesLitterToAllocate(this.matingId);
                if (response.status === STATUS_CODES.SUCCESS) {
                    this.puppyList = response.data.map((puppy, index) => {
                        const puppyIndex = index + 1 || 1
                        const puppyName = puppy.name ? puppy.name : `Puppy ${puppyIndex}`;
                        return { ...puppy, label: puppyName, puppyName };
                    });
                } else {
                    this.showAPIErrorToast(this.toastMessages.litters().ERROR_GET_LITTER_LIST, response);
                }
            } catch (err) {
                this.showGenericErrorToast();
            } finally {
                this.isAPILoading = false;
            }
        },
        checkFormValidations() {
            // mobile number validation
            if (this.allocationDetails.phoneNumber) {
                this.checkMobileFormat();
                this.$v.allocationDetails.phoneNumber.$touch();
            }

            // email validation
            if (this.$v.allocationDetails.emailAddress) {
                this.$v.allocationDetails.emailAddress.$touch();
            }

            // check if deposit amount is greater than the puppy price
            const puppyPrice = this.price !== null ? parseFloat(this.price) : 0;
            const puppyDeposit = this.deposit !== null ? parseFloat(this.deposit) : 0;

            if (puppyDeposit > puppyPrice) {
                this.isDepositInvalid = true;
            } else {
                this.isDepositInvalid = false;
            }

            const isPuppySelected = this.selectedPuppy && this.selectedPuppy.id;

            if (!isPuppySelected) {
                this.isPuppyValid = false;
            } else {
                this.isPuppyValid = true;
            }

            const isEmailAddressValid = this.$v.allocationDetails.emailAddress.email;

            return this.isDepositInvalid || !this.phoneNumberIsValid || !isEmailAddressValid || !isPuppySelected;
        },
        async submitAllocatePuppy() {
            const isFormInvalid = this.checkFormValidations();

            if (isFormInvalid) {
                return;
            }

            const puppyPrice = this.price !== null ? parseFloat(this.price) : 0;
            const puppyDeposit = this.deposit !== null ? parseFloat(this.deposit) : 0;

            this.isAPISubmitLoading = true;

            const payload = {
                formData: {
                    ...this.allocationDetails,
                    litterId: this.selectedPuppy ? this.selectedPuppy.id : null,
                    price: puppyPrice,
                    deposit: puppyDeposit,
                    matingId: parseFloat(this.matingId),
                },
                contractDocument: this.contractDocument && this.contractDocument.size ? this.contractDocument.file : null,
            };

            try {
                const response = await breedingManagementService.allocatePuppy(payload);

                const { SUCCESS_ALLOCATE_PUPPY, ERROR_ALLOCATE_PUPPY } = this.toastMessages.waitlist();

                if (response.status === STATUS_CODES.SUCCESS_POST) {
                    this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_ALLOCATE_PUPPY);
                    this.isAllocatingSuccess = true;
                    this.$router.push(`${BREEDER_ROUTES.WAIT_LIST}?matingId=${this.matingId}`);
                } else {
                    this.showAPIErrorToast(ERROR_ALLOCATE_PUPPY, response);
                    this.isAllocatingSuccess = false;
                }
            } catch (err) {
                this.showGenericErrorToast();
                this.isAllocatingSuccess = false;
            } finally {
                this.isAPISubmitLoading = false;
            }
        },
        calculatePuppyBalance(event, type) {
            this.allowOnlyNumbers(event); // prevents typing for non numeric characters

            const puppyPrice = this.price !== null ? parseFloat(this.price) : 0;
            const puppyDeposit = this.deposit !== null ? parseFloat(this.deposit) : 0;

            this.balance = puppyPrice - puppyDeposit;
        },
        handleSelectPuppy(puppy) {
            if (puppy) {
                this.isPuppyValid = true;
            } else {
                this.isPuppyValid = false;
            }
        },
        hasImageUrl(source) {
            if (!source || !source.imageUrl) {
                return require("@/assets/images/_shared/dog-purchase.png");
            }

            return source.imageUrl;
        },
        hasValidBirthDate(details) {
            if (!details || !details.dateOfBirth) {
                return "Not Specified";
            }
            return this.formatDateWithoutConversion(details.dateOfBirth);
        },
        mobileNumberFormat(event) {
            // check only after form submit
            if (this.$v.allocationDetails.phoneNumber.$dirty) {
                this.checkMobileFormat();
            }
            this.allocationDetails.phoneNumber = this.formatMobileNumberOnChange(event);
        },
        onPasteMobileNumber(event) {
            event.preventDefault(); // Prevent default paste behavior
            const pastedText = event.clipboardData.getData("text/plain");

            const formatMobileNumberOnPaste = this.formatMobileNumberOnPaste(pastedText);
            event.target.value = formatMobileNumberOnPaste;
        },
        checkMobileFormat() {
            let phoneNumber = this.allocationDetails.phoneNumber;
            let validMobile = true;

            let regexp = /^\d{4}-\d{6}$/;

            if (phoneNumber !== null && phoneNumber !== "") {
                validMobile = regexp.test(phoneNumber);
            } else {
                validMobile = true;
            }

            this.phoneNumberIsValid = validMobile;
        },
        onPickFile() {
            this.$refs.fileInput.click();
        },
        clearFileInput(event) {
            event.target.value = "";
        },
        async removeContract() {
            const confirmDeleteResult = await this.$swal({
                title: "Delete Document",
                text: "Are you sure you want to delete the document?",
                showCancelButton: true,
                confirmButtonColor: "#4B8BFF",
                cancelButtonColor: "#d33",
                confirmButtonText: "Confirm",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                showCloseButton: true,
            });

            if (confirmDeleteResult.isConfirmed) {
                this.contractDocument = null;
            }
        },
        async onFilePicked(event) {
            const files = event.target.files;

            // return early to prevent unnecessary code execution
            if (!files || (files && !files.length)) {
                return;
            }

            // validate file if it's valid or doesn't exceed 20mb
            const isFileValid = await this.validateFileValidity(files[0]);

            if (!isFileValid) {
                return;
            }

            const file = event.target.files[0];

            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const fileType = file.name.split(".").at(-1);
                    const fileUrl = this.fileFormat(fileType);

                    this.contractDocument = {
                        fileName: file.name,
                        file: file,
                        url: fileUrl ? fileUrl : e.target.result,
                        isIcon: fileUrl,
                        size: file.size,
                    };
                };
                reader.readAsDataURL(file);
            }
        },
        async validateFileValidity(file) {
            if (!file) {
                return false;
            }

            let errorMessage = "";
            let isValid = true;

            // by default file.size is in bytes (check if it's > 20 MB in bytes)
            if (file.size > DAM_SIRE_DOCUMENT.MAX_SIZE) {
                errorMessage = "File size must not exceed 20 MB.";
                isValid = false;
            }

            const isFileTypeValid = DAM_SIRE_DOCUMENT.ALLOWED_TYPES.includes(file.type);

            if (!isFileTypeValid) {
                errorMessage = `${file.name} has unsupported file format.`;
                isValid = false;
            }

            if (file.name && file.name.length > DAM_SIRE_DOCUMENT.MAX_NAME_LENGTH) {
                errorMessage = `File name is too long.`;
                isValid = false;
            }

            if (!isValid) {
                this.showCustomErrorToast("Unable to upload file.", errorMessage);
            }

            return isValid;
        },
        fileFormat(type) {
            const fileTypeExtensions = {
                doc: require("@/assets/images/file-type/word.svg"),
                docx: require("@/assets/images/file-type/word.svg"),
                pdf: require("@/assets/images/_shared/pdf-icon.png"),
            };

            return fileTypeExtensions[type] || null;
        },
    },
    async beforeRouteLeave(to, from, next) {
        const isAllocationDetailsValid = Object.values(this.allocationDetails).some((value) => value && value.trim() !== "");
        const isPuppySelected = this.selectedPuppy && this.selectedPuppy.id;
        const isContractUploaded = this.contractDocument && this.contractDocument.size;

        const isPriceInvalid = this.price === null || this.price === "";
        const isDepositInvalid = this.deposit === null || this.deposit === "";

        const puppyPrice = isPriceInvalid ? 0 : parseFloat(this.price);
        const puppyDeposit = isDepositInvalid ? 0 : parseFloat(this.deposit);

        if (!this.isAllocatingSuccess && (isAllocationDetailsValid || isPuppySelected || isContractUploaded || puppyPrice || puppyDeposit)) {
            const result = await this.showUnsavedChangesModal();

            if (result.value) {
                next(true);
            } else {
                next(false);
            }
        } else {
            next(true);
        }
    },
    validations() {
        return this.validationRules;
    },
};
